import type { FormEventHandler, ReactNode } from 'react';
import { Loader2, TriangleAlert, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useConfirmBeforeClose } from '@/hooks/useConfirmBeforeClose';
import { cn } from '@/lib/utils';
import { PCMP_EDITOR_ROLE_LABELS } from '../../constants/pcmpProposal.constants';
import type { PcmpProposalCapabilities } from '../../types/pcmpProposal.types';
import { PcmpEditErrorAlert } from './PcmpEditErrorAlert';

interface PcmpEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  icon: LucideIcon;
  /** Who is editing and the after-disbursement warning; left out for non-edit actions. */
  capabilities?: Pick<PcmpProposalCapabilities, 'editorRole' | 'isPostDisbursementEdit'>;
  description?: string;
  isDirty: boolean;
  isPending: boolean;
  error: unknown;
  onSubmit: FormEventHandler<HTMLFormElement>;
  submitLabel?: string;
  submitVariant?: 'default' | 'destructive';
  /** Keeps the submit off while the dialog only explains why the action cannot run. */
  submitDisabled?: boolean | undefined;
  pendingLabel?: string;
  className?: string;
  children: ReactNode;
}

/**
 * The frame every proposal edit shares: who is editing, the after-
 * disbursement warning, the API error inline, and a discard prompt.
 * The caller wraps it in its own `<Form>` provider.
 */
export function PcmpEditDialog({
  open,
  onOpenChange,
  title,
  icon: Icon,
  capabilities,
  isDirty,
  isPending,
  error,
  onSubmit,
  submitLabel = 'Save changes',
  submitVariant = 'default',
  submitDisabled = false,
  pendingLabel = 'Saving...',
  description,
  className,
  children,
}: PcmpEditDialogProps) {
  const { askBeforeClose, discardChangesDialog } = useConfirmBeforeClose(isDirty);

  function requestClose() {
    if (isPending) return;
    askBeforeClose(() => onOpenChange(false));
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : requestClose())}>
        <DialogContent className={cn('flex max-h-[90vh] flex-col gap-0 sm:max-w-lg', className)}>
          <DialogHeader className="-mx-4 -mt-4 flex-row items-center gap-3 border-b p-4 pr-12">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Icon className="size-4" />
            </div>
            <div className="flex min-w-0 flex-col gap-0.5">
              <DialogTitle>{title}</DialogTitle>
              {capabilities?.editorRole ? (
                <DialogDescription className="text-xs">
                  Editing as {PCMP_EDITOR_ROLE_LABELS[capabilities.editorRole]}
                </DialogDescription>
              ) : description ? (
                <DialogDescription className="text-xs">{description}</DialogDescription>
              ) : null}
            </div>
          </DialogHeader>

          <form noValidate onSubmit={onSubmit} className="-mx-4 -mb-4 flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 py-5">
              {capabilities?.isPostDisbursementEdit ? (
                <div className="flex items-start gap-3 rounded-lg bg-destructive px-4 py-3 text-sm text-white">
                  <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
                  <span>
                    This proposal is already disbursed. The change is recorded as a
                    post-disbursement edit.
                  </span>
                </div>
              ) : null}
              {children}
              <PcmpEditErrorAlert error={error} />
            </div>
            <DialogFooter className="mx-0 mb-0">
              <Button type="button" variant="outline" onClick={requestClose} disabled={isPending}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant={submitVariant}
                disabled={isPending || submitDisabled}
                className="gap-1.5"
              >
                {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                {isPending ? pendingLabel : submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {discardChangesDialog}
    </>
  );
}
